import { COLLECTION_SORT_OPTIONS, DEFAULT_COLLECTION_SORT } from '@/lib/collection-sort';

const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
const MARKET_PATTERN = /^[a-z]{2}[/-](?:[a-z]{2}|row)$/i;
const SAFE_CURSOR_PATTERN = /^[\x21-\x7e]+$/;
const PRICE_PATTERN = /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/;
const SHOPIFY_PRODUCT_GID_PATTERN = /^gid:\/\/shopify\/Product\/(\d{1,20})$/;
const NUMERIC_PRODUCT_ID_PATTERN = /^\d{1,20}$/;

const MAX_HANDLE_LENGTH = 255;
const MAX_CURSOR_LENGTH = 1024;
const MAX_FILTER_VALUE_LENGTH = 100;
const MAX_PRODUCT_TYPES = 20;
const MAX_TRADE_FILTERS = 20;
const MAX_PRICE = 1_000_000;

export const PUBLIC_PRODUCT_HANDLE_LIMIT = 24;
export const PUBLIC_FRESH_PRODUCT_HANDLE_LIMIT = 4;
export const PUBLIC_COLLECTION_HANDLE_LIMIT = 12;
export const PUBLIC_PRODUCT_IMAGE_LIMIT = 20;
export const PUBLIC_PRODUCT_LIST_LIMIT = 50;
export const PUBLIC_COLLECTION_PRODUCT_LIMIT = 50;
export const PUBLIC_REVIEW_LIMIT = 20;
export const PUBLIC_REVIEW_OFFSET_LIMIT = 1000;
export const PUBLIC_REVIEW_RATINGS_FANOUT_LIMIT = 50;

export class PublicStorefrontInputError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'PublicStorefrontInputError';
	}
}

function invalid(message: string): never {
	throw new PublicStorefrontInputError(message);
}

function assertAllowedSearchParams(searchParams: URLSearchParams, allowed: ReadonlySet<string>) {
	const seen = new Set<string>();

	for (const [key] of searchParams) {
		if (!allowed.has(key)) {
			invalid(`Unsupported query parameter: ${key}`);
		}
		if (seen.has(key)) {
			invalid(`${key} may only be provided once`);
		}
		seen.add(key);
	}

	const market = searchParams.get('market');
	if (market !== null && !MARKET_PATTERN.test(market)) {
		invalid('market must be a language/country pair');
	}
}

function parseInteger(
	value: string | null,
	name: string,
	defaultValue: number,
	min: number,
	max: number,
) {
	if (value === null) return defaultValue;
	if (!/^\d+$/.test(value)) {
		invalid(`${name} must be an integer from ${min} to ${max}`);
	}

	const parsed = Number(value);
	if (!Number.isSafeInteger(parsed) || parsed < min || parsed > max) {
		invalid(`${name} must be an integer from ${min} to ${max}`);
	}
	return parsed;
}

export function parseStorefrontHandle(value: string | null | undefined, name = 'handle') {
	const handle = value?.trim();
	if (!handle || handle.length > MAX_HANDLE_LENGTH || !HANDLE_PATTERN.test(handle)) {
		return invalid(
			`${name} must be a lowercase Shopify handle of at most ${MAX_HANDLE_LENGTH} characters`,
		);
	}
	return handle;
}

function parseHandleList(value: string | null, name: string, max: number) {
	if (!value) {
		return invalid(`${name} is required`);
	}
	if (value.length > max * (MAX_HANDLE_LENGTH + 1)) {
		return invalid(`${name} may contain at most ${max} handles`);
	}

	const rawHandles = value.split(',');
	if (rawHandles.length > max) {
		return invalid(`${name} may contain at most ${max} handles`);
	}

	const handles = rawHandles.map((handle) => parseStorefrontHandle(handle, name));
	return [...new Set(handles)];
}

function parseOptionalImageCount(searchParams: URLSearchParams) {
	const value = searchParams.get('imageCount');
	return value === null
		? undefined
		: parseInteger(value, 'imageCount', PUBLIC_PRODUCT_IMAGE_LIMIT, 1, PUBLIC_PRODUCT_IMAGE_LIMIT);
}

export function parseProductsQuery(searchParams: URLSearchParams) {
	assertAllowedSearchParams(searchParams, new Set(['handles', 'fresh', 'imageCount', 'market']));
	const handles = parseHandleList(
		searchParams.get('handles'),
		'handles',
		PUBLIC_PRODUCT_HANDLE_LIMIT,
	);
	const freshValue = searchParams.get('fresh');
	if (freshValue !== null && freshValue !== '0' && freshValue !== '1') {
		return invalid('fresh must be 0 or 1');
	}
	const fresh = freshValue === '1';
	if (fresh && handles.length > PUBLIC_FRESH_PRODUCT_HANDLE_LIMIT) {
		return invalid(
			`fresh requests may contain at most ${PUBLIC_FRESH_PRODUCT_HANDLE_LIMIT} handles`,
		);
	}

	return {
		handles,
		fresh,
		imageCount: parseOptionalImageCount(searchParams),
	};
}

export function parseProductCardsQuery(searchParams: URLSearchParams) {
	assertAllowedSearchParams(searchParams, new Set(['handles', 'market']));
	return {
		handles: parseHandleList(searchParams.get('handles'), 'handles', PUBLIC_PRODUCT_HANDLE_LIMIT),
	};
}

export function parseProductImagesQuery(searchParams: URLSearchParams) {
	assertAllowedSearchParams(searchParams, new Set(['handle', 'imageCount', 'market']));
	return {
		handle: parseStorefrontHandle(searchParams.get('handle')),
		imageCount: parseInteger(
			searchParams.get('imageCount'),
			'imageCount',
			PUBLIC_PRODUCT_IMAGE_LIMIT,
			1,
			PUBLIC_PRODUCT_IMAGE_LIMIT,
		),
	};
}

export function parseProductVariantsQuery(searchParams: URLSearchParams) {
	assertAllowedSearchParams(searchParams, new Set(['handle', 'market']));
	return { handle: parseStorefrontHandle(searchParams.get('handle')) };
}

export function parseProductListQuery(searchParams: URLSearchParams) {
	assertAllowedSearchParams(searchParams, new Set(['first', 'sort', 'market']));
	const sort = searchParams.get('sort') ?? 'best-selling';
	if (sort !== 'best-selling' && sort !== 'new-arrivals') {
		return invalid('sort must be best-selling or new-arrivals');
	}

	return {
		first: parseInteger(searchParams.get('first'), 'first', 12, 1, PUBLIC_PRODUCT_LIST_LIMIT),
		sort,
	};
}

export function parseCollectionHandlesQuery(searchParams: URLSearchParams) {
	assertAllowedSearchParams(searchParams, new Set(['handles', 'market']));
	return {
		handles: parseHandleList(
			searchParams.get('handles'),
			'handles',
			PUBLIC_COLLECTION_HANDLE_LIMIT,
		),
	};
}

function parseFilterValues(value: string, name: string, maxValues: number) {
	if (value.length > maxValues * (MAX_FILTER_VALUE_LENGTH + 1)) {
		return invalid(`${name} may contain at most ${maxValues} values`);
	}

	const values = value.split(',');
	if (values.length === 0 || values.length > maxValues) {
		return invalid(`${name} may contain at most ${maxValues} values`);
	}

	const normalized = values.map((entry) => entry.trim());
	for (const entry of normalized) {
		if (
			!entry ||
			entry.length > MAX_FILTER_VALUE_LENGTH ||
			[...entry].some((character) => {
				const codePoint = character.codePointAt(0) ?? 0;
				return codePoint < 32 || codePoint === 127;
			})
		) {
			return invalid(
				`${name} values must be non-empty and at most ${MAX_FILTER_VALUE_LENGTH} characters`,
			);
		}
	}

	return [...new Set(normalized)];
}

function parsePrice(value: string | null, name: string) {
	if (value === null) return undefined;
	if (!PRICE_PATTERN.test(value)) {
		return invalid(`${name} must be a non-negative price with at most two decimal places`);
	}
	const price = Number(value);
	if (!Number.isFinite(price) || price > MAX_PRICE) {
		return invalid(`${name} must not exceed ${MAX_PRICE}`);
	}
	return price;
}

function appendProductTypeFilters(filters: Record<string, unknown>[], productType: string | null) {
	if (productType === null) return;
	for (const value of parseFilterValues(productType, 'filter.product_type', MAX_PRODUCT_TYPES)) {
		filters.push({ productType: value });
	}
}

function appendAvailabilityFilter(filters: Record<string, unknown>[], availability: string | null) {
	if (availability === null) return;
	const values = parseFilterValues(availability, 'filter.availability', 2);
	if (values.some((value) => value !== 'in-stock' && value !== 'out-of-stock')) {
		return invalid('filter.availability only accepts in-stock and out-of-stock');
	}
	if (values.length === 1) {
		filters.push({ available: values[0] === 'in-stock' });
	}
}

function appendPriceFilter(filters: Record<string, unknown>[], searchParams: URLSearchParams) {
	const min = parsePrice(searchParams.get('filter.price.min'), 'filter.price.min');
	const max = parsePrice(searchParams.get('filter.price.max'), 'filter.price.max');
	if (min !== undefined && max !== undefined && min > max) {
		return invalid('filter.price.min must not exceed filter.price.max');
	}
	if (min === undefined && max === undefined) return;

	filters.push({
		price: {
			...(min === undefined ? {} : { min }),
			...(max === undefined ? {} : { max }),
		},
	});
}

function parseCollectionFilters(searchParams: URLSearchParams) {
	const filters: Record<string, unknown>[] = [];
	appendProductTypeFilters(filters, searchParams.get('filter.product_type'));
	appendAvailabilityFilter(filters, searchParams.get('filter.availability'));
	appendPriceFilter(filters, searchParams);

	const trades = searchParams.get('filter.trades');
	if (trades !== null) {
		parseHandleList(trades, 'filter.trades', MAX_TRADE_FILTERS);
	}

	return filters;
}

export function parseCollectionProductsQuery(
	handleValue: string | null | undefined,
	searchParams: URLSearchParams,
) {
	assertAllowedSearchParams(
		searchParams,
		new Set([
			'after',
			'first',
			'sort',
			'market',
			'filter.product_type',
			'filter.availability',
			'filter.price.min',
			'filter.price.max',
			'filter.trades',
		]),
	);

	const afterValue = searchParams.get('after');
	let after: string | undefined;
	if (afterValue) {
		if (afterValue.length > MAX_CURSOR_LENGTH || !SAFE_CURSOR_PATTERN.test(afterValue)) {
			return invalid(`after must be a cursor of at most ${MAX_CURSOR_LENGTH} characters`);
		}
		after = afterValue;
	}

	const sort = searchParams.get('sort') ?? DEFAULT_COLLECTION_SORT;
	if (!Object.hasOwn(COLLECTION_SORT_OPTIONS, sort)) {
		return invalid('Unsupported collection sort');
	}

	const filters = parseCollectionFilters(searchParams);
	const hasFilterParams = [...searchParams.keys()].some((key) => key.startsWith('filter.'));

	return {
		handle: parseStorefrontHandle(handleValue),
		after,
		first: parseInteger(searchParams.get('first'), 'first', 24, 1, PUBLIC_COLLECTION_PRODUCT_LIMIT),
		sort,
		filters,
		hasFilterParams,
	};
}

export function normalizeShopifyProductId(value: string | null | undefined) {
	const productId = value?.trim();
	if (!productId) return null;
	if (NUMERIC_PRODUCT_ID_PATTERN.test(productId)) return productId;
	return SHOPIFY_PRODUCT_GID_PATTERN.exec(productId)?.[1] ?? null;
}

export function parseReviewsQuery(
	productIdValue: string | null | undefined,
	searchParams: URLSearchParams,
) {
	assertAllowedSearchParams(searchParams, new Set(['limit', 'offset', 'sort']));
	const productId = normalizeShopifyProductId(productIdValue);
	if (!productId) {
		return invalid('productId must be a numeric Shopify product ID');
	}

	return {
		productId,
		limit: parseInteger(searchParams.get('limit'), 'limit', 5, 1, PUBLIC_REVIEW_LIMIT),
		offset: parseInteger(searchParams.get('offset'), 'offset', 0, 0, PUBLIC_REVIEW_OFFSET_LIMIT),
		sort: parseInteger(searchParams.get('sort'), 'sort', 3, 0, 5),
	};
}

export function parseRecommendationsQuery(searchParams: URLSearchParams) {
	assertAllowedSearchParams(searchParams, new Set(['productId', 'market']));
	const numericId = normalizeShopifyProductId(searchParams.get('productId'));
	if (!numericId) {
		return invalid('productId must be a Shopify product GID or numeric ID');
	}
	return { productId: `gid://shopify/Product/${numericId}` };
}
